import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CheckIcon, ChevronLeftIcon, HammerIcon, PowerIcon, PowerOffIcon } from 'lucide-react'
import {
	Alert, AlertBanner, AlertTitle,
	Badge,
	Button,
	Card, CardContent, CardHeader, CardTitle,
	Checkbox,
	ConfirmDialog,
	Form,
	IconButton,
	Label,
	Spinner,
} from '@rentflowdev/ui'
import {
	useDeleteBuildProfileMutation,
	useEnqueueBuildMutation,
	useGetBuildProfileQuery,
	useUpdateBuildProfileMutation,
} from '@/api'
import type { BuildProfile } from '@rentflowdev/api'
import { extractErrorMessage } from '@/lib/error-message'
import { usePageTitleStore } from '@/lib/page-title-store'
import { BuildJobsTable } from '@/components/build-jobs-table'
import { BuilderProfileFormFields } from '@/components/builder-profile-dialog/fields'
import {
	builderProfileSchema,
	formValuesToUpdateBody,
	profileToFormValues,
	type BuilderProfileFormValues,
} from '@/components/builder-profile-dialog/schema'

export const Route = createFileRoute('/_app/builder/$profileId')({
	component: BuilderProfilePage,
})

function BuilderProfilePage() {
	const { profileId } = Route.useParams()
	const navigate = useNavigate()
	const setPageTitle = usePageTitleStore((s) => s.setPageTitle)

	const { data: profile, isLoading, error } = useGetBuildProfileQuery(profileId)
	const [enqueueBuild, { isLoading: isEnqueueing }] = useEnqueueBuildMutation()
	const [deleteProfile, { isLoading: isDeleting }] = useDeleteBuildProfileMutation()
	const [updateProfile, { isLoading: isActivating }] = useUpdateBuildProfileMutation()

	const [deactivateOpen, setDeactivateOpen] = useState(false)
	const [buildDialogOpen, setBuildDialogOpen] = useState(false)
	const [publishToRustore, setPublishToRustore] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
	const [toast, setToast] = useState<string | null>(null)
	const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	function showToast(message: string) {
		setToast(message)
		clearTimeout(toastTimer.current)
		toastTimer.current = setTimeout(() => setToast(null), 4000)
	}

	useEffect(() => () => clearTimeout(toastTimer.current), [])

	useEffect(() => {
		setPageTitle(profile ? profile.appName : null)
		return () => setPageTitle(null)
	}, [profile, setPageTitle])

	function openBuildDialog() {
		setPublishToRustore(false)
		setBuildDialogOpen(true)
	}

	async function handleEnqueueBuild() {
		setErrorMsg(undefined)
		try {
			await enqueueBuild({ profileId, publish: publishToRustore }).unwrap()
			setBuildDialogOpen(false)
			showToast('Сборка поставлена в очередь')
		} catch (err) {
			console.error('enqueueBuild failed:', err)
			setBuildDialogOpen(false)
			setErrorMsg(extractErrorMessage(err) ?? 'Не удалось запустить сборку')
		}
	}

	async function handleDeactivate() {
		setErrorMsg(undefined)
		try {
			await deleteProfile(profileId).unwrap()
			setDeactivateOpen(false)
			showToast('Профиль отключён')
		} catch (err) {
			console.error('deleteBuildProfile failed:', err)
			setDeactivateOpen(false)
			setErrorMsg(extractErrorMessage(err) ?? 'Не удалось отключить профиль')
		}
	}

	async function handleActivate() {
		setErrorMsg(undefined)
		try {
			await updateProfile({ id: profileId, body: { isActive: true } }).unwrap()
			showToast('Профиль активирован')
		} catch (err) {
			console.error('updateBuildProfile failed:', err)
			setErrorMsg(extractErrorMessage(err) ?? 'Не удалось активировать профиль')
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center py-24">
				<Spinner />
			</div>
		)
	}

	if (error !== undefined || !profile) {
		return (
			<div className="px-4 py-6">
				<AlertBanner
					variant="destructive"
					title="Не удалось загрузить профиль"
					description={extractErrorMessage(error)}
				/>
			</div>
		)
	}

	return (
		<div className="flex-1 overflow-auto">
			<div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-6">
				<ConfirmDialog
					open={buildDialogOpen}
					onOpenChange={setBuildDialogOpen}
					title="Запустить сборку?"
					description={`Сборка версии ${profile.versionName} (${profile.versionCode}) будет поставлена в очередь.`}
					confirmLabel="Запустить"
					cancelLabel="Отмена"
					loading={isEnqueueing}
					onConfirm={handleEnqueueBuild}
				>
					<div className="flex flex-col gap-1.5">
						<div className="flex items-center gap-2">
							<Checkbox
								id="publish-to-rustore"
								checked={publishToRustore}
								disabled={!profile.hasRustoreCredentials}
								onCheckedChange={(checked) => setPublishToRustore(checked === true)}
							/>
							<Label
								htmlFor="publish-to-rustore"
								className={
									profile.hasRustoreCredentials
										? undefined
										: 'text-(--color-muted-foreground)'
								}
							>
								Опубликовать в RuStore
							</Label>
						</div>
						{!profile.hasRustoreCredentials && (
							<p className="text-sm text-(--color-muted-foreground)">
								Добавьте ключи RuStore в профиле
							</p>
						)}
					</div>
				</ConfirmDialog>

				<ConfirmDialog
					open={deactivateOpen}
					onOpenChange={setDeactivateOpen}
					title="Отключить профиль?"
					description="Профиль будет деактивирован — запускать новые сборки для этого клиента станет нельзя. Историю сборок это не затронет."
					confirmLabel="Отключить"
					cancelLabel="Отмена"
					variant="destructive"
					loading={isDeleting}
					onConfirm={handleDeactivate}
				/>

				<div className="flex flex-wrap items-center gap-3">
					<IconButton
						variant="ghost"
						aria-label="Назад к списку"
						onClick={() => void navigate({ to: '/builder' })}
					>
						<ChevronLeftIcon className="size-5" />
					</IconButton>
					<h1 className="text-2xl font-[500] tracking-[-0.6px] text-(--color-foreground)">
						{profile.appName}
					</h1>
					<Badge variant={profile.isActive ? 'success' : 'muted'}>
						{profile.isActive ? 'Активен' : 'Отключён'}
					</Badge>
					<div className="ml-auto flex items-center gap-2">
						{profile.isActive ? (
							<Button
								variant="destructive"
								iconLeft={<PowerOffIcon className="size-4" />}
								onClick={() => setDeactivateOpen(true)}
							>
								Отключить
							</Button>
						) : (
							<Button
								variant="secondary"
								iconLeft={<PowerIcon className="size-4" />}
								disabled={isActivating}
								onClick={() => void handleActivate()}
							>
								Активировать
							</Button>
						)}
						<Button
							iconLeft={<HammerIcon className="size-4" />}
							disabled={!profile.isActive || isEnqueueing}
							onClick={openBuildDialog}
						>
							Собрать приложение
						</Button>
					</div>
				</div>

				{errorMsg && (
					<AlertBanner variant="destructive" title="Ошибка" description={errorMsg} />
				)}

				{/* Technical identity is server-derived and read-only. */}
				<div className="flex flex-wrap items-center gap-x-8 gap-y-2 rounded-md border border-(--color-border) px-4 py-3">
					<div className="flex flex-col gap-0.5">
						<span className="text-xs text-(--color-muted-foreground)">Application ID</span>
						<span className="font-mono text-sm text-(--color-muted-foreground)">
							{profile.applicationId}
						</span>
					</div>
					<div className="flex flex-col gap-0.5">
						<span className="text-xs text-(--color-muted-foreground)">Слаг</span>
						<span className="font-mono text-sm text-(--color-muted-foreground)">
							{profile.clientSlug}
						</span>
					</div>
					<div className="flex flex-col gap-0.5">
						<span className="text-xs text-(--color-muted-foreground)">Версия</span>
						<span className="text-sm text-(--color-muted-foreground)">
							{profile.versionName} ({profile.versionCode})
						</span>
					</div>
				</div>

				<ProfileEditCard
					// Remount on every saved revision so the form picks up fresh values.
					key={`${profile.id}-${profile.updatedAt}`}
					profile={profile}
					onSaved={showToast}
					onError={setErrorMsg}
				/>

				<section className="flex flex-col gap-3">
					<h2 className="text-base font-[500] text-(--color-foreground)">История сборок</h2>
					<BuildJobsTable profileId={profileId} />
				</section>
			</div>

			{toast && (
				<div className="fixed bottom-6 right-6 z-50">
					<Alert variant="success" className="w-auto shadow-md">
						<CheckIcon className="size-4" />
						<AlertTitle>{toast}</AlertTitle>
					</Alert>
				</div>
			)}
		</div>
	)
}

interface ProfileEditCardProps {
	profile: BuildProfile
	onSaved: (message: string) => void
	onError: (message: string | undefined) => void
}

function ProfileEditCard({ profile, onSaved, onError }: ProfileEditCardProps) {
	const [updateProfile, { isLoading }] = useUpdateBuildProfileMutation()

	const form = useForm<BuilderProfileFormValues>({
		defaultValues: profileToFormValues(profile),
		resolver: zodResolver(builderProfileSchema) as Resolver<BuilderProfileFormValues>,
		mode: 'onBlur',
	})

	async function onSubmit(values: BuilderProfileFormValues) {
		onError(undefined)
		try {
			await updateProfile({ id: profile.id, body: formValuesToUpdateBody(values) }).unwrap()
			onSaved('Изменения сохранены')
		} catch (err) {
			console.error('updateBuildProfile failed:', err)
			onError(extractErrorMessage(err) ?? 'Не удалось сохранить изменения')
		}
	}

	async function handleClearRustoreCredentials() {
		onError(undefined)
		try {
			// Explicit empty strings clear the stored pair (`undefined` keeps it).
			await updateProfile({
				id: profile.id,
				body: { rustoreKeyId: '', rustorePrivateKey: '' },
			}).unwrap()
			onSaved('Ключи RuStore удалены')
		} catch (err) {
			console.error('updateBuildProfile failed:', err)
			onError(extractErrorMessage(err) ?? 'Не удалось удалить ключи')
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Настройки профиля</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
						<BuilderProfileFormFields
							mode="edit"
							hasRustoreCredentials={profile.hasRustoreCredentials}
							onClearRustoreCredentials={() => void handleClearRustoreCredentials()}
							isClearingRustoreCredentials={isLoading}
						/>
						<div className="flex justify-end">
							<Button type="submit" disabled={isLoading}>
								Сохранить
							</Button>
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
